import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  FormControl,
  Autocomplete, Box, Typography
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from 'src/apiConfig';
import { getSchoolInfo } from 'src/api/schoolMetod';
import { regenerateSingleDiploma } from 'src/api/diplomaService';
import { useTranslation } from 'react-i18next';
import { latinToCyrillic } from 'src/helper/helperFunction';

const AddStudentDialog = ({
  open,
  handleClose,
  schoolId,
  onSuccess,
  studentData,
  allSchoolView
}) => {
  const [name, setName] = useState('');
  const [gender, setGender] = useState('');
  const [category, setCategory] = useState('');
  const [classGrade, setClassGrade] = useState('');
  const [professor, setProfessor] = useState('');
  const [language, setLanguage] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [errors, setErrors] = useState({});
  const { t } = useTranslation();
  const [schools, setSchools] = useState([]);
  const [maticnaSkola, setMaticnaSkola] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const schoolsResponse = await axios.get(
          `${API_BASE_URL}/api/schools.php?allSchoolsWithLocation`
        );
        setSchools(
          schoolsResponse.data.map((school) => ({
            id: school.id,
            name: school.name,
            city: school.location_name || ''
          }))
        );

        console.log('schoolsResponse:', schoolsResponse.data[0]);
      } catch (error) {
        console.error('Error fetching schools:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (schoolId || studentData) {
          const data = await getSchoolInfo(
            schoolId ? schoolId : studentData.schoolid
          );

          setSelectedSchool({
            id: data.id,
            name: data.name
          });
        }
        const categoriesResponse = await axios.get(
          `${API_BASE_URL}/api/categories.php`
        );
        setCategories(categoriesResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [schoolId, studentData, professor, open]);

  useEffect(() => {
    if (studentData) {
      setName(studentData.name || '');
      setGender(studentData.pol || '');
      setCategory(studentData.category ?? '');
      setClassGrade(studentData.class || '');
      setProfessor(studentData.professor || '');
      setLanguage(studentData.language || '');
      setSelectedSchool({
        id: studentData.schoolid || '',
        name: studentData.school_name || ''
      });
      setMaticnaSkola(
        studentData.maticna_skola || ''
      );

    } else {
      setName('');
      setGender('');
      setCategory('');
      setClassGrade('');
      setProfessor('');
      setLanguage('');
      setSelectedSchool(null);
      setMaticnaSkola('');
    }
  }, [studentData]);

  const handleCloseDialog = () => {
    setErrors({});
    setName('');
    setGender('');
    setCategory('');
    setClassGrade('');
    setLanguage('');
    setSelectedSchool(null);
    setProfessor('');
    handleClose();
  };

  const handleSchoolChange = async (selectedOption) => {
    if (selectedOption) {
      setSelectedSchool({
        id: selectedOption.id,
        name: selectedOption.name
      });
      try {
        const data = await getSchoolInfo(selectedOption.id);
        setProfessor(data.professor || '');
      } catch (error) {
        console.error('Error fetching school info:', error);
      }
    } else {
      setSelectedSchool(null);
      setProfessor('');
    }
  };

  const handleClassGradeChange = (e) => {
    const value = e.target.value;
    if (
      /^\d*$/.test(value) &&
      (value === '' || (Number(value) >= 1 && Number(value) <= 12))
    ) {
      setClassGrade(value);
      setErrors((prevErrors) => ({ ...prevErrors, classGrade: '' }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        classGrade: t('dialog.gradeError')
      }));
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === 'name') setName(value);
    if (name === 'gender') setGender(value);
    if (name === 'category') setCategory(value);
    if (name === 'classGrade') setClassGrade(value);
    if (name === 'professor') setProfessor(value);
    if (name === 'language') setLanguage(value);
  };


  const handleSubmit = () => {
    let validationErrors = {};


    if (!selectedSchool) {
      validationErrors.school = t('dialog.schoolRequired')
    }
    if (!name) validationErrors.name = t('dialog.nameRequired');
    if (!gender) validationErrors.gender = t('dialog.genderRequired');
    if (category === '' || category === null || category === undefined)
      validationErrors.category = t('dialog.categoryRequired');
    if (!classGrade || Number(classGrade) < 1 || Number(classGrade) > 12)
      validationErrors.classGrade = t('dialog.gradeError');
    if (!language) validationErrors.language = t('dialog.languageRequired');

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const studentDataApi = {
      schoolid: selectedSchool ? selectedSchool.id : schoolId,
      maticna_skola: maticnaSkola ? maticnaSkola : '',
      name,
      pol: gender,
      category,
      class: classGrade,
      professor,
      language
    };

    if (studentData) {
      axios
        .put(`${API_BASE_URL}/api/students.php?id=${studentData.id}`, studentDataApi)
        .then(async () => {
          try {
            await regenerateSingleDiploma(studentData.id);
          } catch (regenerateError) {
            // No diploma exists yet or regeneration failed — student update still succeeds
            console.warn('Diploma regeneration skipped:', regenerateError);
          } finally {
            // Always close and refresh regardless of diploma outcome
            onSuccess();
            handleCloseDialog();
          }
        })
        .catch((error) => {
          console.error('Error updating student:', error);
          alert('Greška pri ažuriranju učenika.');
        });
    } else {
      axios
        .post(`${API_BASE_URL}/api/students.php`, studentDataApi)
        .then(() => {
          onSuccess();
          handleCloseDialog();
        })
        .catch((error) => console.error('Error adding student:', error));
    }
  };

  const filterSchoolOptions = (options, { inputValue }) => {
    const inputCyrillic = latinToCyrillic(inputValue);
    return options.filter((option) =>
      option.name.toLowerCase().includes(inputCyrillic.toLowerCase())
    );
  };

  const getLanguageLabel = (languageCode) => {
    switch (languageCode) {
      case 'С':
        return t('dialog.serbian');
      case 'Х':
        return t('dialog.croatian');
      case 'М':
        return t('dialog.hungarian');
      case 'Л':
        return t('dialog.latin');
      case 'Р':
        return t('dialog.romanian');
      default:
        return '';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          padding: '20px',
          width: '600px'
        }
      }}
    >
      <DialogTitle
        sx={{ fontWeight: 'bold', fontSize: '1.5rem', color: '#2596be' }}
      >
        {schoolId && selectedSchool
          ? `${t('dialog.schoolLabel')}: ${selectedSchool.name}`
          : t('meni.addNewStudent')}
      </DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          padding: '0 20px'
        }}
      >
        <Box
          sx={{
            p: 2,
            mb: 1,
            backgroundColor: '#e3f2fd',
            border: '1px solid #90caf9',
            borderRadius: '10px',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1565c0', mb: 0.5 }}>
            {t('dialog.diplomaFieldsNote')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#1565c0' }}  >
            {t('dialog.diplomaSchoolNote')}
          </Typography>
        </Box>
        {allSchoolView ? (
          <FormControl fullWidth>
            <Autocomplete
              options={schools}
              getOptionLabel={(option) =>
                `${option.name}${option.city ? ` - ${option.city}` : ''}`
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={filterSchoolOptions}
              value={selectedSchool || null}
              onChange={(_, newValue) => handleSchoolChange(newValue)}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.name} - {option.city}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('dialog.schoolLabel')}
                  variant="outlined"
                  error={!!errors.school}
                  helperText={errors.school}
                  sx={{ borderRadius: '8px' }}
                />
              )}
              noOptionsText={t('pagination.noRowsLabel')}
            />
          </FormControl>
        ) : (
          <TextField
            label={t('dialog.schoolName')}
            value={selectedSchool ? selectedSchool.name : ''}
            disabled
            fullWidth
            sx={{ marginBottom: '20px', borderRadius: '8px' }}
          />
        )}
        <FormControl fullWidth>
          <TextField
            label={t('dialog.maticnaLabel')}
            placeholder={t('dialog.maticnaPlaceholder')}
            value={maticnaSkola || ''}
            onChange={(e) => setMaticnaSkola(e.target.value)}
            fullWidth
            helperText={t('dialog.maticnaHelperText')}
            sx={{ borderRadius: '8px' }}
          />
        </FormControl>

        <TextField
          label={t('dialog.studentNameLabel')}
          value={name}
          onChange={handleChange}
          name="name"
          fullWidth
          error={!!errors.name}
          helperText={errors.name}
          sx={{ borderRadius: '8px' }}
        />

        <FormControl fullWidth>
          <Autocomplete
            options={[
              { value: 'm', label: t('dialog.male') },
              { value: 'z', label: t('dialog.female') }
            ]}
            getOptionLabel={(option) => option.label}
            value={
              gender
                ? {
                  value: gender,
                  label:
                    gender === 'm' ? t('dialog.male') : t('dialog.female')
                }
                : null
            }
            onChange={(_, newValue) =>
              setGender(newValue ? newValue.value : '')
            }
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('dialog.genderLabel')}
                variant="outlined"
                error={!!errors.gender}
                helperText={errors.gender}
                sx={{ borderRadius: '8px' }}
              />
            )}
            noOptionsText={t('pagination.noRowsLabel')}
          />
        </FormControl>

        <FormControl fullWidth>
          <Autocomplete
            options={categories}
            getOptionLabel={(option) => option.notes}
            value={
              category !== null && category !== '' && category !== undefined
                ? categories.find((cat) => String(cat.id) === String(category)) || null
                : null
            }
            onChange={(_, newValue) => setCategory(newValue ? newValue.id : '')}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('dialog.categoryLabel')}
                variant="outlined"
                error={!!errors.category}
                helperText={errors.category}
                sx={{ borderRadius: '8px' }}
              />
            )}
            noOptionsText={t('pagination.noRowsLabel')}
          />
        </FormControl>

        <TextField
          label={t('dialog.classGradeLabel')}
          type="number"
          value={classGrade}
          onChange={handleClassGradeChange}
          fullWidth
          error={!!errors.classGrade}
          helperText={errors.classGrade}
          sx={{ borderRadius: '8px' }} // Radius za input
          inputProps={{ min: 1, max: 12 }}
        />

        <TextField
          label={t('dialog.professorLabel')}
          value={professor}
          onChange={handleChange}
          name="professor"
          fullWidth
          error={!!errors.professor}
          helperText={errors.professor}
          sx={{ borderRadius: '8px' }} // Radius za input
        />

        <FormControl fullWidth>
          <Autocomplete
            options={[
              { value: 'С', label: t('dialog.serbian') },
              { value: 'Х', label: t('dialog.croatian') },
              { value: 'М', label: t('dialog.hungarian') },
              { value: 'Л', label: t('dialog.latin') },
              { value: 'Р', label: t('dialog.romanian') }
            ]}
            getOptionLabel={(option) => option.label}
            value={
              language
                ? { value: language, label: getLanguageLabel(language) }
                : null
            }
            onChange={(_, newValue) =>
              setLanguage(newValue ? newValue.value : '')
            }
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('dialog.languageLabel')}
                variant="outlined"
                error={!!errors.language}
                helperText={errors.language}
                sx={{ borderRadius: '8px' }}
              />
            )}
            noOptionsText={t('pagination.noRowsLabel')}
          />
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ padding: '10px 20px' }}>
        <Button onClick={handleCloseDialog} sx={{ color: '#137eac' }}>
          {t('dialog.cancelButton')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            backgroundColor: '#2596be',
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: '#137eac'
            }
          }}
        >
          {!studentData ? t('dialog.submitButton') : t('dialog.edit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddStudentDialog;
